import { Flex, Stack } from '@mantine/core';
import { useGoals } from '../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { OverviewCard } from '../../../components/overview-card/overview-card';
import type { VerticalBarCardData } from '../../../components/overview-card/overview-card.type';
import { ProgressBarCard } from '../../../components/overview-card/progress-bar-card';
import { VerticalBarCard } from '../../../components/overview-card/vertical-bar-card';
import { round } from '../../../helpers/math-helpers/math-helpers';
import {
  graphLightRed,
  graphPictonBlue,
  graphPistachio,
  graphSunglow,
  skyBase,
  skyDark,
} from '../../../styles/design-tokens';
import { H3, Text } from '../../../ui-library/typography/typography';

export const GoalsOverview = () => {
  const i18n = useTranslation();
  const { goals } = useGoals();

  // Calculate status data
  const onTrackGoals = goals.filter((g) => g.status?.by_tasks?.status === 'on_track').length;
  const atRiskGoals = goals.filter((g) => g.status?.by_tasks?.status === 'at_risk').length;
  const offTrackGoals = goals.filter((g) => g.status?.by_tasks?.status === 'off_track').length;

  const statusesData: VerticalBarCardData = [
    { category: 'Off Track', value: offTrackGoals, fill: graphLightRed },
    { category: 'At Risk', value: atRiskGoals, fill: graphSunglow },
    { category: 'On Track', value: onTrackGoals, fill: graphPistachio },
  ];

  // Calculate state data
  const noEndDateGoals = goals.filter((g) => !g.end_date).length;
  const noStartDateGoals = goals.filter((g) => !g.start_date).length;

  const statusesExtraData: VerticalBarCardData = [
    { category: 'No Start Date', value: noStartDateGoals, fill: skyBase },
    { category: 'No End Date', value: noEndDateGoals, fill: graphPictonBlue },
  ];

  // Calculate overall completion
  const overallCompletion =
    goals.length > 0 ? round(goals.reduce((sum, goal) => sum + (goal.percent_progress || 0), 0) / goals.length, 2) : 0;

  return (
    <Flex direction="row" gap={24}>
      <VerticalBarCard
        title={`${i18n.t('common.strategic_goals')} by Statuses`}
        icon="insert_chart_outlined"
        data={statusesData}
      />
      <VerticalBarCard
        title={`${i18n.t('common.strategic_goals')} by State`}
        icon="insert_chart_outlined"
        data={statusesExtraData}
      />
      <ProgressBarCard
        title={`Overall ${i18n.t('common.strategic_goals')} Completion`}
        icon="check_circle"
        value={overallCompletion}
      />
      <OverviewCard
        title="Items"
        icon="show_chart"
        content={
          <Stack justify="center" align="center" gap={0} mt={10}>
            <H3>{goals.length}</H3>
            <Text size="tiny" lineHeight="tight" color={skyDark}>
              {`Total ${i18n.t('common.strategic_goals')}`}
            </Text>
          </Stack>
        }
      />
    </Flex>
  );
};
